'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

export type TooltipVariant = 'default' | 'dark' | 'light';

// ============================================================================
// Popover Context
// ============================================================================

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement>;
  placement: PopoverPlacement;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

export function usePopoverContext() {
  const context = React.useContext(PopoverContext);
  if (!context) {
    throw new Error('usePopoverContext must be used within Popover');
  }
  return context;
}

// ============================================================================
// Popover
// ============================================================================

export interface PopoverProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: PopoverPlacement;
  children: React.ReactNode;
}

export function Popover({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom',
  children,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const triggerRef = React.useRef<HTMLElement>(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );

  const contextValue: PopoverContextValue = {
    open,
    setOpen,
    triggerRef,
    placement,
  };

  return (
    <PopoverContext.Provider value={contextValue}>
      <div className="popover-root relative inline-block">{children}</div>
    </PopoverContext.Provider>
  );
}

// ============================================================================
// PopoverTrigger
// ============================================================================

export interface PopoverTriggerProps {
  children: React.ReactElement;
  asChild?: boolean;
}

export function PopoverTrigger({ children, asChild = false }: PopoverTriggerProps) {
  const { open, setOpen, triggerRef } = usePopoverContext();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(!open);
  };

  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      ref: triggerRef,
      onClick: handleClick,
      'aria-expanded': open,
      'aria-haspopup': 'dialog',
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button
      type="button"
      ref={triggerRef as React.RefObject<HTMLButtonElement>}
      onClick={handleClick}
      aria-expanded={open}
      aria-haspopup="dialog"
    >
      {children}
    </button>
  );
}

// ============================================================================
// PopoverContent
// ============================================================================

export interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
}

export function PopoverContent({
  sideOffset = 8,
  align = 'center',
  className,
  children,
  ...props
}: PopoverContentProps) {
  const { open, setOpen, placement } = usePopoverContext();
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, setOpen]);

  if (!open) return null;

  const placementClasses: Record<PopoverPlacement, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    'top-start': 'bottom-full left-0 mb-2',
    'top-end': 'bottom-full right-0 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    'bottom-start': 'top-full left-0 mt-2',
    'bottom-end': 'top-full right-0 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    'left-start': 'right-full top-0 mr-2',
    'left-end': 'right-full bottom-0 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    'right-start': 'left-full top-0 ml-2',
    'right-end': 'left-full bottom-0 ml-2',
  };

  return (
    <div
      ref={contentRef}
      role="dialog"
      className={clsx(
        'popover-content absolute z-50',
        'min-w-[200px] p-4',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        'rounded-lg shadow-lg',
        'animate-in fade-in zoom-in-95 duration-150',
        placementClasses[placement],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// PopoverClose
// ============================================================================

export interface PopoverCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

export function PopoverClose({
  asChild = false,
  children,
  className,
  onClick,
  ...props
}: PopoverCloseProps) {
  const { setOpen } = usePopoverContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    setOpen(false);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button
      type="button"
      className={clsx('popover-close', className)}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

// ============================================================================
// PopoverArrow
// ============================================================================

export interface PopoverArrowProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
}

export function PopoverArrow({ size = 8, className, ...props }: PopoverArrowProps) {
  const { placement } = usePopoverContext();

  const arrowClasses: Record<string, string> = {
    top: 'bottom-[-4px] left-1/2 -translate-x-1/2 rotate-45',
    'top-start': 'bottom-[-4px] left-4 rotate-45',
    'top-end': 'bottom-[-4px] right-4 rotate-45',
    bottom: 'top-[-4px] left-1/2 -translate-x-1/2 rotate-45',
    'bottom-start': 'top-[-4px] left-4 rotate-45',
    'bottom-end': 'top-[-4px] right-4 rotate-45',
    left: 'right-[-4px] top-1/2 -translate-y-1/2 rotate-45',
    'left-start': 'right-[-4px] top-4 rotate-45',
    'left-end': 'right-[-4px] bottom-4 rotate-45',
    right: 'left-[-4px] top-1/2 -translate-y-1/2 rotate-45',
    'right-start': 'left-[-4px] top-4 rotate-45',
    'right-end': 'left-[-4px] bottom-4 rotate-45',
  };

  return (
    <div
      className={clsx(
        'popover-arrow absolute',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        arrowClasses[placement],
        className
      )}
      style={{ width: size, height: size }}
      {...props}
    />
  );
}

// ============================================================================
// Tooltip
// ============================================================================

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: PopoverPlacement;
  variant?: TooltipVariant;
  delay?: number;
  disabled?: boolean;
}

export function Tooltip({
  content,
  children,
  placement = 'top',
  variant = 'default',
  delay = 200,
  disabled = false,
}: TooltipProps) {
  const [open, setOpen] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => setOpen(true), delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(false);
  };

  const handleFocus = () => {
    if (!disabled) setOpen(true);
  };

  const handleBlur = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const placementClasses: Record<PopoverPlacement, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    'top-start': 'bottom-full left-0 mb-2',
    'top-end': 'bottom-full right-0 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    'bottom-start': 'top-full left-0 mt-2',
    'bottom-end': 'top-full right-0 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    'left-start': 'right-full top-0 mr-2',
    'left-end': 'right-full bottom-0 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    'right-start': 'left-full top-0 ml-2',
    'right-end': 'left-full bottom-0 ml-2',
  };

  const variantClasses = {
    default: 'bg-gray-900 dark:bg-gray-700 text-white',
    dark: 'bg-black text-white',
    light: 'bg-white text-gray-900 border border-gray-200 shadow-lg dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700',
  };

  return (
    <div className="tooltip-root relative inline-block">
      {React.cloneElement(children, {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onFocus: handleFocus,
        onBlur: handleBlur,
        'aria-describedby': open ? 'tooltip' : undefined,
      } as React.HTMLAttributes<HTMLElement>)}

      {open && (
        <div
          role="tooltip"
          id="tooltip"
          className={clsx(
            'tooltip absolute z-50 px-2 py-1',
            'text-sm rounded whitespace-nowrap',
            'animate-in fade-in zoom-in-95 duration-150',
            variantClasses[variant],
            placementClasses[placement]
          )}
        >
          {content}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TooltipProvider
// ============================================================================

export interface TooltipProviderProps {
  children: React.ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
}

export function TooltipProvider({
  children,
  delayDuration = 200,
  skipDelayDuration = 300,
}: TooltipProviderProps) {
  // In a full implementation, this would provide context for tooltip timing
  return <>{children}</>;
}

// ============================================================================
// HoverCard
// ============================================================================

export interface HoverCardProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  openDelay?: number;
  closeDelay?: number;
}

export function HoverCard({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  openDelay = 200,
  closeDelay = 300,
}: HoverCardProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );

  return (
    <PopoverContext.Provider
      value={{
        open,
        setOpen,
        triggerRef: React.useRef(null),
        placement: 'bottom',
      }}
    >
      <div className="hover-card-root relative inline-block">{children}</div>
    </PopoverContext.Provider>
  );
}

// ============================================================================
// HoverCardTrigger
// ============================================================================

export interface HoverCardTriggerProps {
  children: React.ReactElement;
  asChild?: boolean;
}

export function HoverCardTrigger({ children, asChild = false }: HoverCardTriggerProps) {
  const { setOpen } = usePopoverContext();
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => setOpen(true), 200);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setTimeout(() => setOpen(false), 300);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
    </span>
  );
}

// ============================================================================
// HoverCardContent
// ============================================================================

export interface HoverCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

export function HoverCardContent({
  side = 'bottom',
  align = 'center',
  className,
  children,
  ...props
}: HoverCardContentProps) {
  const { open, setOpen } = usePopoverContext();

  const handleMouseEnter = () => {
    // Keep open when hovering content
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  if (!open) return null;

  const sideClasses = {
    top: 'bottom-full mb-2',
    bottom: 'top-full mt-2',
    left: 'right-full mr-2',
    right: 'left-full ml-2',
  };

  const alignClasses = {
    start: side === 'top' || side === 'bottom' ? 'left-0' : 'top-0',
    center:
      side === 'top' || side === 'bottom'
        ? 'left-1/2 -translate-x-1/2'
        : 'top-1/2 -translate-y-1/2',
    end: side === 'top' || side === 'bottom' ? 'right-0' : 'bottom-0',
  };

  return (
    <div
      className={clsx(
        'hover-card-content absolute z-50',
        'min-w-[280px] p-4',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        'rounded-lg shadow-lg',
        'animate-in fade-in zoom-in-95 duration-200',
        sideClasses[side],
        alignClasses[align],
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// ContextMenu
// ============================================================================

export interface ContextMenuProps {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}

export function ContextMenu({ children, onOpenChange }: ContextMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      onOpenChange?.(newOpen);
    },
    [onOpenChange]
  );

  return (
    <ContextMenuContext.Provider value={{ open, setOpen: handleOpenChange, position, setPosition }}>
      {children}
    </ContextMenuContext.Provider>
  );
}

interface ContextMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  position: { x: number; y: number };
  setPosition: (pos: { x: number; y: number }) => void;
}

const ContextMenuContext = React.createContext<ContextMenuContextValue | null>(null);

function useContextMenuContext() {
  const context = React.useContext(ContextMenuContext);
  if (!context) {
    throw new Error('useContextMenuContext must be used within ContextMenu');
  }
  return context;
}

// ============================================================================
// ContextMenuTrigger
// ============================================================================

export interface ContextMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export function ContextMenuTrigger({ children, asChild = false }: ContextMenuTriggerProps) {
  const { setOpen, setPosition } = useContextMenuContext();

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setPosition({ x: e.clientX, y: e.clientY });
    setOpen(true);
  };

  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      onContextMenu: handleContextMenu,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return <div onContextMenu={handleContextMenu}>{children}</div>;
}

// ============================================================================
// ContextMenuContent
// ============================================================================

export interface ContextMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {}

export function ContextMenuContent({ className, children, ...props }: ContextMenuContentProps) {
  const { open, setOpen, position } = useContextMenuContext();
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div
      ref={contentRef}
      role="menu"
      className={clsx(
        'context-menu-content fixed z-50',
        'min-w-[160px] py-1',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        'rounded-lg shadow-lg',
        'animate-in fade-in zoom-in-95 duration-100',
        className
      )}
      style={{ left: position.x, top: position.y }}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// ContextMenuItem
// ============================================================================

export interface ContextMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  destructive?: boolean;
  icon?: React.ReactNode;
  shortcut?: string;
}

export function ContextMenuItem({
  disabled = false,
  destructive = false,
  icon,
  shortcut,
  className,
  children,
  onClick,
  ...props
}: ContextMenuItemProps) {
  const { setOpen } = useContextMenuContext();

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    onClick?.(e);
    setOpen(false);
  };

  return (
    <div
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      className={clsx(
        'context-menu-item flex items-center gap-2 px-3 py-2',
        'text-sm cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed',
        destructive
          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
      {shortcut && (
        <span className="text-xs text-gray-400 dark:text-gray-500">{shortcut}</span>
      )}
    </div>
  );
}

// ============================================================================
// ContextMenuSeparator
// ============================================================================

export interface ContextMenuSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

export function ContextMenuSeparator({ className, ...props }: ContextMenuSeparatorProps) {
  return (
    <div
      role="separator"
      className={clsx('context-menu-separator my-1 h-px bg-gray-200 dark:bg-gray-700', className)}
      {...props}
    />
  );
}

// ============================================================================
// DropdownMenu (Convenience Component)
// ============================================================================

export interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  placement?: PopoverPlacement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function DropdownMenu({
  trigger,
  children,
  placement = 'bottom-start',
  open,
  onOpenChange,
}: DropdownMenuProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange} placement={placement}>
      <PopoverTrigger asChild>{trigger as React.ReactElement}</PopoverTrigger>
      <PopoverContent className="p-1 min-w-[160px]">{children}</PopoverContent>
    </Popover>
  );
}

// ============================================================================
// DropdownMenuItem
// ============================================================================

export interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  icon?: React.ReactNode;
}

export function DropdownMenuItem({
  disabled = false,
  icon,
  className,
  children,
  onClick,
  ...props
}: DropdownMenuItemProps) {
  const { setOpen } = usePopoverContext();

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    onClick?.(e);
    setOpen(false);
  };

  return (
    <div
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      className={clsx(
        'dropdown-menu-item flex items-center gap-2 px-3 py-2',
        'text-sm rounded cursor-pointer',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-gray-100 dark:hover:bg-gray-700',
        'text-gray-700 dark:text-gray-300',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
    </div>
  );
}

// ============================================================================
// InfoTooltip
// ============================================================================

export interface InfoTooltipProps {
  content: React.ReactNode;
  placement?: PopoverPlacement;
}

export function InfoTooltip({ content, placement = 'top' }: InfoTooltipProps) {
  return (
    <Tooltip content={content} placement={placement}>
      <button
        type="button"
        className="inline-flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-500 dark:text-gray-500"
        aria-label="More information"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
    </Tooltip>
  );
}

// ============================================================================
// usePopover Hook
// ============================================================================

export interface UsePopoverOptions {
  defaultOpen?: boolean;
  placement?: PopoverPlacement;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface UsePopoverReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  getPopoverProps: () => PopoverProps;
  getTriggerProps: () => {
    onClick: () => void;
    'aria-expanded': boolean;
  };
}

export function usePopover(options: UsePopoverOptions = {}): UsePopoverReturn {
  const { defaultOpen = false, placement = 'bottom', onOpen, onClose } = options;
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const open = React.useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = React.useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  const getPopoverProps = React.useCallback(
    (): PopoverProps => ({
      open: isOpen,
      onOpenChange: (newOpen) => {
        if (newOpen) {
          open();
        } else {
          close();
        }
      },
      placement,
      children: null as unknown as React.ReactNode,
    }),
    [isOpen, open, close, placement]
  );

  const getTriggerProps = React.useCallback(
    () => ({
      onClick: toggle,
      'aria-expanded': isOpen,
    }),
    [toggle, isOpen]
  );

  return {
    isOpen,
    open,
    close,
    toggle,
    getPopoverProps,
    getTriggerProps,
  };
}

// ============================================================================
// useTooltip Hook
// ============================================================================

export interface UseTooltipOptions {
  delay?: number;
  placement?: PopoverPlacement;
}

export interface UseTooltipReturn {
  isOpen: boolean;
  show: () => void;
  hide: () => void;
  getTooltipProps: () => {
    role: 'tooltip';
    className: string;
  };
  getTriggerProps: () => {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
}

export function useTooltip(options: UseTooltipOptions = {}): UseTooltipReturn {
  const { delay = 200 } = options;
  const [isOpen, setIsOpen] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const show = React.useCallback(() => {
    timeoutRef.current = setTimeout(() => setIsOpen(true), delay);
  }, [delay]);

  const hide = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(false);
  }, []);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getTooltipProps = React.useCallback(
    () => ({
      role: 'tooltip' as const,
      className: clsx(
        'absolute z-50 px-2 py-1',
        'bg-gray-900 text-white text-sm rounded',
        'animate-in fade-in duration-150'
      ),
    }),
    []
  );

  const getTriggerProps = React.useCallback(
    () => ({
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: () => setIsOpen(true),
      onBlur: () => setIsOpen(false),
    }),
    [show, hide]
  );

  return {
    isOpen,
    show,
    hide,
    getTooltipProps,
    getTriggerProps,
  };
}
